import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { Link } from 'react-router-dom';
import { useLiveopsChange } from '@/hooks/useLiveopsChange';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { createLatestGuard } from '@/utils/latestRequest';
import {
  audienceLabel,
  countryFlag,
  dayLabel,
  istClock,
  istDayKey,
  istDayStart,
} from '@/utils/cricketCalendar';
import {
  LANGUAGE_SHORT,
  MOMENT_LABEL,
  OPEN_STATUSES,
  PLAN_MOMENTS,
  STATUS_COLOR,
  STATUS_LABEL,
  audienceCount,
  deliveredCount,
  planLanguages,
  planTimeLabel,
} from '@/utils/matchPushCopy';
import type {
  CricketCalendarSettings,
  CricketSyncStatus,
  MatchPushPlan,
  MatchPushSettings,
  MatchPushStatus,
  PlanMoment,
  UpdateMatchPushPlanBody,
} from '@/types/matchPushes';
import { PlanEditModal } from './PlanEditModal';
import { PreviewPanel } from './PreviewPanel';
import { SendTestModal } from './SendTestModal';

const { Text } = Typography;
const DAY_MS = 24 * 60 * 60 * 1000;

type RangeKey = 'today' | 'next7' | 'sent';
const RANGES: Array<{ label: string; value: RangeKey }> = [
  { label: 'Matches today', value: 'today' },
  { label: 'Next 7 days', value: 'next7' },
  { label: 'Sent', value: 'sent' },
];

interface Props {
  /** `?fixture=` from the calendar: show only that match's plans. */
  fixtureId?: string;
  onClearFixture: () => void;
  onOpenSettings: () => void;
}

const num = (n: number): string => n.toLocaleString('en-IN');

const matchWhen = (iso: string): string =>
  `${dayLabel(istDayKey(new Date(iso).getTime()))}, ${istClock(iso)} IST`;

const audienceText = (plan: MatchPushPlan): string => {
  const delivered = plan.status === 'sent' ? deliveredCount(plan.stats) : null;
  return audienceCount(delivered ?? plan.estimatedAudience, plan.status === 'sent');
};

/** Live Ops → Match Pushes → Upcoming: every push the system plans to send, by match. */
export const UpcomingTab: React.FC<Props> = ({ fixtureId, onClearFixture, onOpenSettings }) => {
  const [range, setRange] = useState<RangeKey>('today');
  const [country, setCountry] = useState<string | undefined>();
  const [moment, setMoment] = useState<PlanMoment | undefined>();

  const [plans, setPlans] = useState<MatchPushPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<MatchPushStatus | null>(null);
  const [syncStatus, setSyncStatus] = useState<CricketSyncStatus | null>(null);
  const [settings, setSettings] = useState<MatchPushSettings | null>(null);
  const [calendarSettings, setCalendarSettings] = useState<CricketCalendarSettings | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [editing, setEditing] = useState<MatchPushPlan | null>(null);
  const [testing, setTesting] = useState<MatchPushPlan | null>(null);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const loadGuard = useRef(createLatestGuard());
  const { canChange, deniedTitle } = useLiveopsChange();

  // Side information; failures here do not stop the queue.
  useEffect(() => {
    adminApi.matchPushes
      .getStatus()
      .then((r) => setStatus(r.data))
      .catch(() => undefined);
    adminApi.cricketCalendar
      .getSyncStatus()
      .then((r) => setSyncStatus(r.data))
      .catch(() => undefined);
    adminApi.matchPushes
      .getSettings()
      .then((r) => setSettings(r.data))
      .catch(() => undefined);
    adminApi.cricketCalendar
      .getSettings()
      .then((r) => setCalendarSettings(r.data))
      .catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    const isLatest = loadGuard.current.begin();
    setLoading(true);
    setError(null);
    try {
      const todayStart = istDayStart(istDayKey(Date.now())).getTime();
      let from: number;
      let to: number;
      let planStatus: 'sent' | undefined;
      if (fixtureId) {
        // The night-before push is the day before the match; results can land after midnight.
        const fixture = (await adminApi.cricketCalendar.getFixture(fixtureId)).data;
        if (!isLatest()) return;
        const start = new Date(fixture.startsAt).getTime();
        from = start - 3 * DAY_MS;
        to = start + 3 * DAY_MS;
      } else if (range === 'today') {
        from = todayStart;
        to = todayStart + DAY_MS;
      } else if (range === 'next7') {
        from = todayStart;
        to = todayStart + 7 * DAY_MS;
      } else {
        from = todayStart - 7 * DAY_MS;
        to = todayStart + DAY_MS;
        planStatus = 'sent';
      }
      const response = await adminApi.matchPushes.queue({
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        country,
        moment,
        status: fixtureId ? undefined : planStatus,
      });
      if (!isLatest()) return;
      const items = fixtureId
        ? response.data.items.filter((p) => p.fixtureId === fixtureId)
        : response.data.items;
      setPlans(items);
    } catch (e) {
      if (!isLatest()) return;
      setError(apiErrorMessage(e, 'Failed to load the queue'));
      setPlans([]);
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [fixtureId, range, country, moment]);

  useEffect(() => {
    load();
  }, [load]);

  const replacePlan = (next: MatchPushPlan) => {
    setPlans((list) => list.map((p) => (p.id === next.id ? next : p)));
    setPreviewVersion((v) => v + 1);
  };

  const change = async (plan: MatchPushPlan, body: UpdateMatchPushPlanBody, done: string) => {
    setBusyIds((ids) => new Set(ids).add(plan.id));
    try {
      const response = await adminApi.matchPushes.updatePlan(plan.id, body);
      replacePlan({ ...plan, ...response.data, fixture: response.data.fixture ?? plan.fixture });
      // A note means the change did not do what was asked (e.g. a resume that came too late).
      if (response.data.note) message.warning(response.data.note, 8);
      else message.success(done);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Failed to change the push'));
    } finally {
      setBusyIds((ids) => {
        const next = new Set(ids);
        next.delete(plan.id);
        return next;
      });
    }
  };

  const groups = useMemo(() => {
    const map = new Map<string, MatchPushPlan[]>();
    for (const plan of plans) map.set(plan.fixtureId, [...(map.get(plan.fixtureId) ?? []), plan]);
    return [...map.values()];
  }, [plans]);

  const selected = plans.find((p) => p.id === selectedId) ?? null;
  const countryOptions = useMemo(
    () =>
      (calendarSettings?.trackedCountries ?? []).map((c) => ({
        value: c,
        label: `${countryFlag(c)} ${c}`,
      })),
    [calendarSettings]
  );
  const mode = settings?.languageMode ?? 'experiment';

  const planRow = (plan: MatchPushPlan) => {
    const open = OPEN_STATUSES.includes(plan.status);
    const busy = busyIds.has(plan.id);
    const canEdit = open || (plan.status === 'skipped' && plan.kind === 'fixed');
    return (
      <Space size={2} wrap>
        <Button type="link" size="small" onClick={() => setSelectedId(plan.id)}>
          Preview
        </Button>
        <Tooltip title={deniedTitle}>
          <Space size={2} wrap>
            {canEdit && (
              <Button
                type="link"
                size="small"
                disabled={!canChange}
                onClick={() => setEditing(plan)}
              >
                Edit
              </Button>
            )}
            {plan.status === 'paused' ? (
              <Button
                type="link"
                size="small"
                loading={busy}
                disabled={!canChange}
                onClick={() =>
                  change(
                    plan,
                    { status: plan.kind === 'fixed' ? 'scheduled' : 'waiting' },
                    'Resumed'
                  )
                }
              >
                Resume
              </Button>
            ) : (
              ['scheduled', 'waiting', 'due'].includes(plan.status) && (
                <Button
                  type="link"
                  size="small"
                  loading={busy}
                  disabled={!canChange}
                  onClick={() => change(plan, { status: 'paused' }, 'Paused')}
                >
                  Pause
                </Button>
              )
            )}
            {open && (
              <Popconfirm
                title="Cancel this push?"
                description="It will not be sent. Cancelled pushes cannot be undone from here."
                okText="Cancel push"
                cancelText="Keep"
                disabled={!canChange}
                onConfirm={() => change(plan, { status: 'cancelled' }, 'Cancelled')}
              >
                <Button type="link" size="small" danger loading={busy} disabled={!canChange}>
                  Cancel
                </Button>
              </Popconfirm>
            )}
            {plan.status !== 'cancelled' && (
              <Button
                type="link"
                size="small"
                disabled={!canChange}
                onClick={() => setTesting(plan)}
              >
                Send test
              </Button>
            )}
          </Space>
        </Tooltip>
      </Space>
    );
  };

  const columns = [
    {
      title: 'Time',
      key: 'time',
      width: 96,
      render: (_: unknown, plan: MatchPushPlan) => <Text>{planTimeLabel(plan)}</Text>,
    },
    {
      title: 'Moment',
      key: 'moment',
      render: (_: unknown, plan: MatchPushPlan) => (
        <div>
          <Text strong={plan.id === selectedId}>{MOMENT_LABEL[plan.moment]}</Text>
          {plan.moment === 'result' && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                win copy / loss copy
              </Text>
            </div>
          )}
          {plan.copyOverride && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                custom copy for this match
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Languages',
      key: 'languages',
      render: (_: unknown, plan: MatchPushPlan) => (
        <Space size={2} wrap>
          {planLanguages(plan.audienceCountries, mode).map((l) => (
            <Tag key={l} style={{ marginInlineEnd: 0 }}>
              {LANGUAGE_SHORT[l]}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Players',
      key: 'audience',
      render: (_: unknown, plan: MatchPushPlan) => <Text>{audienceText(plan)}</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, plan: MatchPushPlan) => (
        <div>
          <Tag color={STATUS_COLOR[plan.status]} style={{ marginInlineEnd: 0 }}>
            {plan.status === 'sent' && plan.sentAt
              ? `Sent · ${
                  deliveredCount(plan.stats) !== null
                    ? num(deliveredCount(plan.stats) as number)
                    : istClock(plan.sentAt)
                }`
              : plan.status === 'waiting'
                ? plan.moment === 'toss'
                  ? 'Waiting for toss'
                  : 'Waiting'
                : STATUS_LABEL[plan.status]}
          </Tag>
          {plan.status === 'waiting' && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                checks every 5 min
              </Text>
            </div>
          )}
          {plan.skipReason && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {plan.skipReason}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, plan: MatchPushPlan) => planRow(plan),
    },
  ];

  const list = (
    <>
      {groups.map((group) => {
        const f = group[0].fixture;
        return (
          <Card
            key={group[0].fixtureId}
            size="small"
            style={{ marginBottom: 14 }}
            styles={{ body: { padding: 0 } }}
            title={
              <div>
                <Text strong>
                  {f.team1Name} vs {f.team2Name}
                </Text>
                <Text type="secondary" style={{ fontWeight: 400 }}>
                  {' '}
                  · {[f.matchLabel, f.venue].filter(Boolean).join(' · ') || f.seriesName}
                </Text>
              </div>
            }
            extra={
              <Text type="secondary" style={{ fontSize: 13 }}>
                {matchWhen(f.startsAt)} · to {audienceLabel(group[0].audienceCountries)}{' '}
                {group[0].audienceCountries.map(countryFlag).join(' ')}
              </Text>
            }
          >
            <Table
              size="small"
              showHeader={false}
              pagination={false}
              rowKey="id"
              columns={columns}
              dataSource={[...group].sort(
                (a, b) => PLAN_MOMENTS.indexOf(a.moment) - PLAN_MOMENTS.indexOf(b.moment)
              )}
              scroll={{ x: 'max-content' }}
              onRow={(plan) => ({
                style: plan.id === selectedId ? { background: 'rgba(22,119,255,0.08)' } : undefined,
              })}
            />
          </Card>
        );
      })}
    </>
  );

  return (
    <div>
      {status && !status.on && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Automatic sending is off, so nothing below goes out by itself."
          description="Test sends still work. The switch is on the Settings tab."
          action={
            <Button size="small" onClick={onOpenSettings}>
              Settings
            </Button>
          }
        />
      )}
      <Space wrap style={{ marginBottom: 12, width: '100%' }}>
        {fixtureId ? (
          <>
            <Tag color="blue">One match only</Tag>
            <Button size="small" onClick={onClearFixture}>
              Show all matches
            </Button>
          </>
        ) : (
          <Segmented options={RANGES} value={range} onChange={(v) => setRange(v as RangeKey)} />
        )}
        <Select
          allowClear
          placeholder="All countries"
          style={{ width: 150 }}
          options={countryOptions}
          value={country}
          onChange={setCountry}
        />
        <Select
          allowClear
          placeholder="All moments"
          style={{ width: 160 }}
          options={PLAN_MOMENTS.map((m) => ({ value: m, label: MOMENT_LABEL[m] }))}
          value={moment}
          onChange={setMoment}
        />
        {syncStatus && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {syncStatus.lastRunAt
              ? `Sportskeeda checked ${istClock(syncStatus.lastRunAt)} IST`
              : 'Sportskeeda not checked yet'}{' '}
            · {syncStatus.callsToday} calls today
          </Text>
        )}
      </Space>
      {!fixtureId && range === 'today' && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
          Listed by match day: the night-before push for tomorrow's match is under that match (Next
          7 days), not here.
        </Text>
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Row gutter={20} align="top">
        <Col xs={24} xl={selected ? 16 : 24}>
          {loading && plans.length === 0 ? (
            <Spin />
          ) : plans.length === 0 && !error ? (
            <Alert
              type="info"
              showIcon
              message={
                fixtureId
                  ? 'No pushes are planned for this match. Is it tracked in the calendar?'
                  : 'No pushes in this window.'
              }
              action={
                <Link to="/cricket-calendar">
                  <Button size="small">Open calendar</Button>
                </Link>
              }
            />
          ) : (
            <Spin spinning={loading}>{list}</Spin>
          )}
        </Col>
        {selected && (
          <Col xs={24} xl={8}>
            <div style={{ position: 'sticky', top: 12 }}>
              <PreviewPanel plan={selected} version={String(previewVersion)} />
              <Button type="link" onClick={() => setSelectedId(null)}>
                Close preview
              </Button>
            </div>
          </Col>
        )}
      </Row>
      <PlanEditModal
        plan={editing}
        settings={settings}
        onClose={() => setEditing(null)}
        onSaved={(next) => {
          replacePlan(next);
          setEditing(null);
        }}
      />
      <SendTestModal plan={testing} onClose={() => setTesting(null)} />
    </div>
  );
};
